import Cookies from 'js-cookie';
import { apiAction, logEntry } from '../utils/index';

import {
  GET_ALL_COMPANION_CONFIG_VERSION_DETAILS,
  COMPANION_CONFIG_VERSION_IS_LOADING,
  COMPANION_CONFIG_VERSION_ERROR,
  SET_COMPANION_CONFIG_VERSION_CURRENT_PAGE,
  SET_COMPANION_CONFIG_VERSION_CHANGE,
  UPDATE_COMPANION_CONFIG_VERSION_SAVE_STATUS,
  COMPANION_CONFIG_VERSION_SAVE_ERROR,
  SET_COMPANION_CONFIG_VERSION_DETAILS
} from '../constants/index';
import {
  COMPANION_CONFIG_VERSION_MANAGEMENT,
  CREATE_COMPANION_CONFIG_VERSION,
  UPDATE_COMPANION_CONFIG_VERSION,
  PUBLISH_COMPANION_CONFIG_VERSION,
  DELETE_COMPANION_CONFIG_VERSION
} from '../constants/logs';

const envName = process.env.REACT_APP_ENV_NAME_SHORT;

// BCC-17: read-only companion app config version history (BCC-09 API).
// Mirrors src/actions/modelFirmwareVersion.js's GET-list shape, but the
// failure path deliberately does NOT auto-hide after 2500ms like firmware's
// Banner-driven error does - a failed load must stay visible with a retry
// affordance, not disappear and look identical to "no versions".
//
// NOTE: uses REACT_APP_API_ENDPOINT_ADMIN1, not ADMIN - /management/model/
// companionconfig* is defined in swagger-admin-control_1.yaml (the "_1"
// admin API stack), same as /management/modelcategory (see
// src/actions/modelCategory.js). Model firmware's /management/model/
// firmware lives in the *other*, non-"_1" stack, which is why
// modelFirmwareVersion.js correctly uses plain ADMIN - don't copy that
// variable name here even though the rest of the shape mirrors it.

export const getCompanionConfigVersionsThunk = (modelId, offset, limit, widgetCode) => dispatch => {
  dispatch(getCompanionConfigVersion(modelId, offset, limit, widgetCode));
};

export const setCompanionConfigVersionLoading = isLoading => ({
  type: COMPANION_CONFIG_VERSION_IS_LOADING,
  payload: { isLoading }
});

export const getCompanionConfigVersion = (modelId, offset, limit, widgetCode) =>
  apiAction({
    url: `${process.env.REACT_APP_API_ENDPOINT_ADMIN1}/management/model/companionconfig`,
    data: {
      ModelId: modelId,
      Offset: offset,
      Limit: limit
    },
    method: 'GET',
    onLoad: status => setCompanionConfigVersionLoading(status),
    onSuccess: retData => setAllCompanionConfigVersionListThunk(retData),
    onFailure: err => setCompanionConfigVersionErrorThunk(err),
    accessToken: Cookies.get(`access-${envName}`),
    widgetcode: widgetCode
  });

export const setAllCompanionConfigVersionListThunk = retData => dispatch => {
  let companionConfigVersions = (retData && retData.companionConfigVersions) || [];
  dispatch(setAllCompanionConfigVersionList(companionConfigVersions));
};

// NOTE: deliberately does not touch displayMessageCode. A successful load
// has nothing to say - the field is only ever read for the persistent error
// row's text (errorMessageText, shown only when hasLoadError is true) and
// for the delete/save Banner (showBanner/isOpSuccessful/displayMessageCode,
// shared with the add/edit form's action creators). Since a delete always
// triggers an immediate refetch on the list screen (success or failure -
// see CompanionConfigVersionList.jsx), overwriting displayMessageCode here
// used to silently swap a still-visible delete error/success banner's text
// to this load's own "fetch successful" message before its own 2500ms
// auto-hide fired. Leaving this field alone on success fixes that at the
// source rather than trying to time the refetch around the banner.
export const setAllCompanionConfigVersionList = companionConfigVersions => ({
  type: GET_ALL_COMPANION_CONFIG_VERSION_DETAILS,
  payload: {
    data: {
      companionConfigVersions: companionConfigVersions,
      isLoading: false,
      hasLoadError: false,
      hasLoaded: true
    }
  }
});

export const setCompanionConfigVersionErrorThunk = err => dispatch => {
  let messageCode = err && err.response && err.response.data && err.response.data.errorMessage;
  messageCode = messageCode && messageCode.replace('[400]=>', '');
  dispatch(setCompanionConfigVersionError(messageCode));
};

export const setCompanionConfigVersionError = messageCode => ({
  type: COMPANION_CONFIG_VERSION_ERROR,
  payload: {
    data: {
      companionConfigVersions: [],
      isLoading: false,
      hasLoadError: true,
      hasLoaded: false,
      displayMessageCode: messageCode || ''
    }
  }
});

export const setCurrentPage = currentPage => ({
  type: SET_COMPANION_CONFIG_VERSION_CURRENT_PAGE,
  payload: { currentPage }
});

// BCC-18: add-form (create) actions below. Unlike the list's load-error
// above, a save failure here uses the same transient-Banner convention every
// other save flow in this app uses (see setModelFirmwareVersionStatusThunk) -
// there's no "looks like empty" ambiguity for a save action the way there is
// for a list, so the persistent-error treatment doesn't apply here.

export const changeCompanionConfigVersion = selectedCompanionConfigVersion => ({
  type: SET_COMPANION_CONFIG_VERSION_CHANGE,
  payload: { selectedCompanionConfigVersion }
});

export const postCompanionConfigVersionThunk = (companionConfigVersion, widgetCode) => dispatch => {
  let actionName = companionConfigVersion.ModelCompanionConfigVersionId === 0 ? CREATE_COMPANION_CONFIG_VERSION : UPDATE_COMPANION_CONFIG_VERSION;
  if (companionConfigVersion.Publish) actionName = PUBLISH_COMPANION_CONFIG_VERSION;
  let log = logEntry(COMPANION_CONFIG_VERSION_MANAGEMENT, actionName, companionConfigVersion);
  dispatch(saveCompanionConfigVersion(companionConfigVersion, log, widgetCode));
};

export const saveCompanionConfigVersion = (companionConfigVersion, log, widgetCode) =>
  apiAction({
    url: `${process.env.REACT_APP_API_ENDPOINT_ADMIN1}/management/model/companionconfig/save`,
    method: 'POST',
    widgetcode: widgetCode,
    onLoad: status => setCompanionConfigVersionLoading(status),
    onSuccess: retData => setCompanionConfigVersionStatusThunk(retData),
    onFailure: err => setCompanionConfigVersionSaveErrorThunk(err),
    accessToken: Cookies.get(`access-${envName}`),
    data: {
      CompanionConfigVersion: companionConfigVersion,
      log: log
    }
  });

// NOTE: unlike the list/describe GET endpoints (which resolve to
// { statusCode, message, ... }), save/update/publish/delete all use the
// backend's bare `callback(null, MSG_STRING)` convention - the resolved
// value here IS the message code itself, not an object with a .message
// property. Matches saveModelFirmwareVersion's onSuccess (message => ...)
// treating its argument the same way - don't "fix" this to retData.message.
export const setCompanionConfigVersionStatusThunk = message => dispatch => {
  dispatch(setCompanionConfigVersionStatus(message, true));
  setTimeout(() => {
    // Hide the banner
    dispatch(setCompanionConfigVersionStatus(message, false));
  }, 2500);
};

export const setCompanionConfigVersionStatus = (messageCode, status) => ({
  type: UPDATE_COMPANION_CONFIG_VERSION_SAVE_STATUS,
  payload: {
    data: {
      isOpSuccessful: true,
      showBanner: status,
      isLoading: false,
      displayMessageCode: messageCode || ''
    }
  }
});

// A successful save/publish sets isOpSuccessful: true permanently - nothing
// else ever flips it back to false (showBanner auto-hides after 2500ms, but
// isOpSuccessful itself is untouched). Without this, the *next* mount of
// the add/edit form (Add New, or Edit on any row, any time later) sees a
// stale isOpSuccessful: true + showBanner: false from the *previous* save
// and immediately <Navigate>s itself back to the list on the very first
// render, before the form is ever shown. Dispatched from the form's
// unmount cleanup alongside changeCompanionConfigVersion's field reset.
export const resetCompanionConfigVersionStatus = () => ({
  type: UPDATE_COMPANION_CONFIG_VERSION_SAVE_STATUS,
  payload: {
    data: {
      isOpSuccessful: false,
      showBanner: false,
      isLoading: false,
      displayMessageCode: ''
    }
  }
});

export const setCompanionConfigVersionSaveErrorThunk = err => dispatch => {
  let messageCode = err && err.response && err.response.data && err.response.data.errorMessage;
  messageCode = messageCode && messageCode.replace('[400]=>', '');
  dispatch(setCompanionConfigVersionSaveError(messageCode, true));
  setTimeout(() => {
    // Hide the banner
    dispatch(setCompanionConfigVersionSaveError(messageCode, false));
  }, 2500);
};

export const setCompanionConfigVersionSaveError = (messageCode, status) => ({
  type: COMPANION_CONFIG_VERSION_SAVE_ERROR,
  payload: {
    data: {
      isOpSuccessful: false,
      showBanner: status,
      isLoading: false,
      displayMessageCode: messageCode || ''
    }
  }
});

// Edit: describe a single version (GET .../companionconfig?ModelCompanionConfigVersionId=X
// returns { companionConfigVersion: {...} } - singular key, unlike the
// list's plural companionConfigVersions).
export const describeCompanionConfigVersionThunk = (modelCompanionConfigVersionId, widgetCode) => dispatch => {
  dispatch(describeCompanionConfigVersion(modelCompanionConfigVersionId, widgetCode));
};

export const describeCompanionConfigVersion = (modelCompanionConfigVersionId, widgetCode) =>
  apiAction({
    url: `${process.env.REACT_APP_API_ENDPOINT_ADMIN1}/management/model/companionconfig`,
    data: {
      ModelCompanionConfigVersionId: modelCompanionConfigVersionId
    },
    method: 'GET',
    onLoad: status => setCompanionConfigVersionLoading(status),
    onSuccess: retData => setDescribeCompanionConfigVersionThunk(retData),
    onFailure: err => setCompanionConfigVersionSaveErrorThunk(err),
    accessToken: Cookies.get(`access-${envName}`),
    widgetcode: widgetCode
  });

export const setDescribeCompanionConfigVersionThunk = retData => dispatch => {
  dispatch(setDescribeCompanionConfigVersion(retData && retData.companionConfigVersion));
};

export const setDescribeCompanionConfigVersion = selectedCompanionConfigVersion => ({
  type: SET_COMPANION_CONFIG_VERSION_DETAILS,
  payload: { selectedCompanionConfigVersion }
});

// Delete (draft only - backend rejects a Published row). Returns the
// dispatch's promise (apiMiddleware.js returns the axios promise for API
// actions, and both this thunk and the container's bindActionCreators wrapper
// propagate it) so the list screen can refetch once the delete settles,
// success or failure, without a separate "did it work" action type.
export const deleteCompanionConfigVersionThunk = (companionConfigVersion, widgetCode) => dispatch => {
  let log = logEntry(COMPANION_CONFIG_VERSION_MANAGEMENT, DELETE_COMPANION_CONFIG_VERSION, companionConfigVersion);
  return dispatch(deleteCompanionConfigVersion(companionConfigVersion, log, widgetCode));
};

export const deleteCompanionConfigVersion = (companionConfigVersion, log, widgetCode) =>
  apiAction({
    url: `${process.env.REACT_APP_API_ENDPOINT_ADMIN1}/management/model/companionconfig/delete`,
    method: 'POST',
    widgetcode: widgetCode,
    onLoad: status => setCompanionConfigVersionLoading(status),
    onSuccess: retData => setCompanionConfigVersionStatusThunk(retData),
    onFailure: err => setCompanionConfigVersionSaveErrorThunk(err),
    accessToken: Cookies.get(`access-${envName}`),
    data: {
      CompanionConfigVersion: companionConfigVersion,
      log: log
    }
  });
